/**
 * UnreadCountContext
 * 
 * Manages unread message count via a single Realtime subscription per user session.
 * Replaces repeated polling of /rest/v1/messages?recipient_id=eq.<user>&is_read=eq.false
 * 
 * Features:
 * - Single Realtime channel subscription per user
 * - Tracks unread count in memory
 * - Initial load: One query to get baseline count
 * - Listens for INSERT (new messages with is_read=false)
 * - Listens for UPDATE (when is_read status changes)
 * - Automatic cleanup on unmount
 * - React StrictMode guard to prevent duplicate subscriptions
 * - Uses centralized auth (useAuth) - no direct supabase.auth calls
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';

const UnreadCountContext = createContext(null);

export function UnreadCountProvider({ children }) {
  const { user } = useAuth() || {};
  const userId = user?.id;
  
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const channelRef = useRef(null);
  const mountedRef = useRef(true);
  const subscriptionActiveRef = useRef(false);

  // Load initial unread count
  const loadInitialCount = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', userId)
        .eq('is_read', false);

      if (error) throw error;

      if (mountedRef.current) {
        setUnreadCount(count || 0);
      }
    } catch (err) {
      console.error('[UnreadCountContext] Error loading initial count:', err);
      if (mountedRef.current) {
        setUnreadCount(0);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [userId]);

  // Subscribe to message changes via Realtime
  useEffect(() => {
    if (!userId) {
      setUnreadCount(0);
      return;
    }

    // Load initial count
    loadInitialCount();

    // Prevent duplicate subscriptions in React StrictMode
    if (subscriptionActiveRef.current && channelRef.current) {
      return;
    }

    // Create channel for this user's messages
    const channel = supabase.channel(`unread_count_${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `recipient_id=eq.${userId}`,
        },
        (payload) => {
          // New message arrived for this user
          // Only increment if it's unread (is_read=false by default on INSERT)
          if (payload.new?.is_read === false) {
            if (mountedRef.current) {
              setUnreadCount((prev) => prev + 1);
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `recipient_id=eq.${userId}`,
        },
        (payload) => {
          // Message status changed
          const wasRead = payload.old?.is_read === true;
          const isNowRead = payload.new?.is_read === true;

          if (!wasRead && isNowRead) {
            // Transitioned from unread → read: decrement
            if (mountedRef.current) {
              setUnreadCount((prev) => Math.max(0, prev - 1));
            }
          } else if (wasRead && !isNowRead) {
            // Transitioned from read → unread: increment
            if (mountedRef.current) {
              setUnreadCount((prev) => prev + 1);
            }
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          subscriptionActiveRef.current = true;
          console.log(`[UnreadCountContext] Realtime subscribed for user ${userId}`);
        } else if (status === 'CLOSED') {
          subscriptionActiveRef.current = false;
          console.log(`[UnreadCountContext] Realtime unsubscribed for user ${userId}`);
        }
      });

    channelRef.current = channel;

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
        subscriptionActiveRef.current = false;
      }
    };
  }, [userId, loadInitialCount]);

  // Track mounting state
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const value = {
    unreadCount,
    loading,
    refresh: loadInitialCount, // Allow manual refresh if needed
  };

  return (
    <UnreadCountContext.Provider value={value}>
      {children}
    </UnreadCountContext.Provider>
  );
}

export function useUnreadCount() {
  const ctx = useContext(UnreadCountContext);
  if (!ctx) {
    return {
      unreadCount: 0,
      loading: false,
      refresh: () => {},
    };
  }
  return ctx;
}
